/**
 * Glue between the shell and the Staff Hub (WP4-HR), mounted once in the
 * signed-in shell:
 *
 * - "My Hub" first in the avatar menu, with the count of overdue items
 *   (expired licenses, overdue sign-offs) on the item and on the avatar.
 *   Refreshed on `hr` live events, on focus and every five minutes, so a
 *   renewal uploaded elsewhere clears the badge.
 * - Actions the familiar menus name: `hr.my-hub`, `hr.team` (Dentrix
 *   Office Manager > Maintenance > Practice Setup > Staff / Employees) and
 *   `hr.compliance` (Reports > Employee Compliance, the grid tab).
 */
import { useQuery } from '@tanstack/react-query';
import { HubSummarySchema } from '@velora/contracts/hr';
import { useNavigate } from '@tanstack/react-router';
import { useEffect, useRef } from 'react';
import { registerAction } from '../actions/registry.js';
import { api } from '../api.js';
import { registerCommand } from '../commands/registry.js';
import { registerUserMenuItem } from '../layout/TopBarParts.js';
import { usePermission } from '../session/session.js';

export const HUB_SUMMARY_KEY = ['hr', 'summary'] as const;

export function hubBadgeLabel(overdue: number): string {
  return overdue === 1 ? '1 overdue item in My Hub' : `${overdue} overdue items in My Hub`;
}

/** Renders nothing; keeps the avatar badge and the Staff Hub verbs registered. */
export function HubBridge() {
  const navigate = useNavigate();
  const nav = useRef(navigate);
  nav.current = navigate;
  const canSelf = usePermission('hr.self', { locationId: null });
  const canTeam = usePermission('hr.view', { locationId: null });

  const summary = useQuery({
    queryKey: HUB_SUMMARY_KEY,
    queryFn: ({ signal }) => api.request('/hr/me/summary', { schema: HubSummarySchema, signal }),
    enabled: canSelf,
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    retry: false,
  });
  const overdue = summary.data?.overdue ?? 0;

  useEffect(() => {
    if (!canSelf) return undefined;
    return registerUserMenuItem({
      id: 'hr.my-hub',
      label: 'My Hub',
      placement: 'top',
      order: 10,
      ...(overdue > 0 ? { badge: overdue, badgeLabel: hubBadgeLabel(overdue) } : {}),
      onSelect: () => void nav.current({ to: '/me' } as never),
    });
  }, [canSelf, overdue]);

  useEffect(() => {
    const offs: Array<() => void> = [];
    if (canSelf) {
      offs.push(
        registerAction({
          id: 'hr.my-hub',
          label: 'My Hub',
          icon: 'user-round',
          run: () => void nav.current({ to: '/me' } as never),
        }),
      );
    }
    if (canTeam) {
      const openTeam = (tab?: string) =>
        void nav.current({
          to: '/settings/team',
          ...(tab ? { search: (prev: Record<string, unknown>) => ({ ...prev, tab }) } : {}),
        } as never);
      offs.push(
        registerAction({
          id: 'hr.team',
          label: 'Staff / Employees',
          icon: 'users-round',
          run: () => openTeam(),
        }),
        registerAction({
          id: 'hr.compliance',
          label: 'Employee Compliance',
          icon: 'shield-check',
          run: () => openTeam('compliance'),
        }),
        registerCommand({
          id: 'hr.compliance',
          title: 'Employee compliance',
          group: 'Navigate',
          keywords: [
            'license expiry',
            'cpr',
            'dea',
            'osha',
            'inspection',
            'sign-offs',
            'Staff Hub',
          ],
          icon: 'shield-check',
          permission: 'hr.view',
          weight: 1,
          run: () => openTeam('compliance'),
        }),
      );
    }
    return () => {
      for (const off of offs) off();
    };
  }, [canSelf, canTeam]);

  return null;
}
